import { Listbox } from '@headlessui/react';
import {
  ChartBarIcon,
  CreditCardIcon,
  HeartIcon,
  LogoutIcon,
  QuestionMarkCircleIcon,
  UserCircleIcon,
  UserGroupIcon,
  GiftIcon,
} from '@heroicons/react/outline';

import Link from 'next/link';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import { ImHistory } from 'react-icons/im';

import clsxm from '@/lib/clsxm';
import { logout } from '@/utils/auth';
import { useUser } from '@/utils/useUser';

import { SimpleTooltip } from '@/components/ui/tooltip';

const userMenuItems = [
  { name: 'My Profile', href: '/settings', icon: UserCircleIcon },
  { name: 'My Team', href: '/settings/team', icon: UserGroupIcon },
  { name: 'Subscription', href: '/settings/billing', icon: CreditCardIcon },
  { name: 'Usage', href: '/settings/usage', icon: ChartBarIcon },
  { name: 'History', href: '/history', icon: ImHistory },
  { name: 'Favourites', href: '/history/favorites', icon: HeartIcon },
  { name: 'Refer a friend', href: '/settings/referrals', icon: GiftIcon },
  { name: 'Help & Support', href: '/support', icon: QuestionMarkCircleIcon },
] as const;

interface UserDropdownProps {
  align?: 'left' | 'right';
  className?: string;
  [key: string]: any;
}

export default function UserDropdown({
  align = 'right',
  className,
  ...props
}: UserDropdownProps) {
  const { t } = useLocale('dashboard');
  const { user, isFreePlan } = useUser();
  const router = useRouter();

  if (!user) {
    return null;
  }

  return (
    <Listbox as='div' className={className} {...props}>
      <Listbox.Label className='sr-only'>User menu</Listbox.Label>
      <SimpleTooltip title={t('Account')} side='bottom'>
        <Listbox.Button
          className='flex h-6 w-6 items-center justify-center rounded-lg shadow-md shadow-black/5 ring-1 ring-black/5 dark:bg-slate-700 dark:ring-inset dark:ring-white/5'
          aria-label='User menu'
        >
          <UserCircleIcon className='h-4 w-4 stroke-slate-400 dark:stroke-slate-400' />
        </Listbox.Button>
      </SimpleTooltip>
      <Listbox.Options
        className={clsxm(
          'absolute top-full z-50 mt-3 w-48 space-y-1 rounded-xl bg-white p-3 text-sm font-medium shadow-md shadow-black/5 ring-1 ring-black/5 dark:bg-slate-800 dark:ring-white/5',
          align === 'right' ? 'right-0' : 'left-0',
        )}
      >
        {isFreePlan && (
          <Link
            href='/settings/billing'
            className='flex cursor-pointer items-center rounded-[0.625rem] p-2 text-primary-500 hover:bg-slate-100 dark:hover:bg-slate-900/40'
          >
            <div className='rounded-md bg-white p-1 shadow ring-1 ring-slate-900/5 dark:bg-slate-700 dark:ring-inset dark:ring-white/5'>
              <svg
                className='h-4 w-4 fill-primary-400'
                viewBox='0 0 16 16'
                aria-hidden='true'
              >
                <path d='M8 1a.75.75 0 01.75.75v1.5a.75.75 0 01-1.5 0v-1.5A.75.75 0 018 1zm3.5 4.5a.75.75 0 011.06 0l1.06 1.06a.75.75 0 11-1.06 1.06l-1.06-1.06a.75.75 0 010-1.06zm-8.06 0a.75.75 0 010 1.06L2.38 7.62a.75.75 0 11-1.06-1.06l1.06-1.06a.75.75 0 011.06 0zM8 7a1 1 0 100 2 1 1 0 000-2zm-4 1a4 4 0 118 0 4 4 0 01-8 0zm-2.25.75a.75.75 0 000 1.5h1.5a.75.75 0 000-1.5h-1.5zm11 0a.75.75 0 000 1.5h1.5a.75.75 0 000-1.5h-1.5zM4.56 11.44a.75.75 0 00-1.06 1.06l1.06 1.06a.75.75 0 001.06-1.06l-1.06-1.06zm7.94 0a.75.75 0 010 1.06l1.06 1.06a.75.75 0 001.06-1.06l-1.06-1.06a.75.75 0 00-1.06 0zM8 12.25a.75.75 0 01.75.75v1.5a.75.75 0 01-1.5 0V13a.75.75 0 01.75-.75z' />
              </svg>
            </div>
            <div className='ml-3'>{t('Upgrade plan')}</div>
          </Link>
        )}

        {userMenuItems.map((item) => (
          <Link
            key={item.name}
            href={item.href}
            className={clsxm(
              'flex cursor-pointer items-center rounded-[0.625rem] p-2',
              router.pathname.includes(item.href)
                ? 'text-primary-500'
                : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-900/40 dark:hover:text-white',
            )}
          >
            <div className='rounded-md bg-white p-1 shadow ring-1 ring-slate-900/5 dark:bg-slate-700 dark:ring-inset dark:ring-white/5'>
              <item.icon
                className={clsxm(
                  'h-4 w-4',
                  router.pathname.includes(item.href)
                    ? 'text-primary-400'
                    : 'text-slate-400',
                )}
              />
            </div>
            <div className='ml-3'>{t(item.name)}</div>
          </Link>
        ))}

        <div className='my-1 border-t border-slate-200 dark:border-slate-700' />

        <button
          onClick={logout}
          className='flex w-full cursor-pointer items-center rounded-[0.625rem] p-2 text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-900/40 dark:hover:text-white'
        >
          <div className='rounded-md bg-white p-1 shadow ring-1 ring-slate-900/5 dark:bg-slate-700 dark:ring-inset dark:ring-white/5'>
            <LogoutIcon className='h-4 w-4 stroke-slate-400' />
          </div>
          <div className='ml-3'>{t('Logout')}</div>
        </button>
      </Listbox.Options>
    </Listbox>
  );
}
